import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import API_URL from './config';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const [isSupported, setIsSupported] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscribersCount, setSubscribersCount] = useState(0);
  const [isIOS, setIsIOS] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');

  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [sending, setSending] = useState(false);

  const VAPID_PUBLIC_KEY = 'BEWKqvKMza6lS2a5nBTzFSoVuYCCTHVrEkHuuvixe1RKW7mFTvQgTcBpPA_mfGdl6I04R9vToCCjsQ3f5n8FIAQ';

  const addDebugLog = useCallback((message) => {
    console.log(message);
    setDebugInfo(prev => prev + '\n' + message);
  }, []);

  // Авторизация
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (data.success || data.token) {
        setIsAuthenticated(true);
        localStorage.setItem('crm_auth', 'true');
        addDebugLog('✅ Авторизован');
      } else {
        setLoginError('Неверный пароль');
      }
    } catch (error) {
      setLoginError('Ошибка соединения');
      addDebugLog(`❌ Ошибка входа: ${error.message}`);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('crm_auth');
    setPassword('');
    addDebugLog('👋 Выход');
  };

  // Заказы
  const loadOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/orders`);
      const newOrders = await response.json();

      // Сравниваем старые и новые заказы
      const hasChanges = JSON.stringify(orders) !== JSON.stringify(newOrders);

      if (hasChanges) {
        setOrders(newOrders);
        addDebugLog(`📦 Заказы обновлены (${newOrders.length})`);
      }
    } catch (error) {
      addDebugLog(`❌ Ошибка загрузки заказов: ${error.message}`);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        addDebugLog(`✅ Статус заказа #${orderId} изменен на ${newStatus}`);
        loadOrders();
      }
    } catch (error) {
      addDebugLog(`❌ Ошибка обновления статуса: ${error.message}`);
    }
  };

  const deleteOrder = async (orderId) => {
    if (window.confirm('Удалить заказ?')) {
      try {
        const response = await fetch(`${API_URL}/orders/${orderId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          addDebugLog(`🗑 Заказ #${orderId} удален`);
          loadOrders();
        }
      } catch (error) {
        addDebugLog(`❌ Ошибка удаления: ${error.message}`);
      }
    }
  };

  // Push-уведомления
  const getSubscribersCount = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/subscribers-count`);
      const data = await response.json();
      setSubscribersCount(data.count);
    } catch (error) {
      console.error('Ошибка:', error);
    }
  }, []);

  const checkSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        setIsSubscribed(true);
        setSubscription(sub);
        addDebugLog('✅ Уже подписан');
      } else {
        addDebugLog('❌ Не подписан');
      }
    } catch (error) {
      addDebugLog(`Ошибка проверки: ${error.message}`);
    }
  }, [addDebugLog]);

  const initServiceWorker = useCallback(async () => {
    try {
      await navigator.serviceWorker.register('/sw.js');
      addDebugLog('✅ Service Worker зарегистрирован');
    } catch (error) {
      addDebugLog(`❌ Ошибка SW: ${error.message}`);
    }
  }, [addDebugLog]);

  useEffect(() => {
    addDebugLog('📱 Приложение загружено');

    const savedAuth = localStorage.getItem('crm_auth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
    addDebugLog(`iOS: ${iOS}`);

    const isStandalone = window.navigator.standalone;
    addDebugLog(`Запущено с главного экрана: ${isStandalone}`);

    if (iOS && !isStandalone) {
      addDebugLog('⚠️ Для уведомлений добавьте на главный экран!');
    }

    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    addDebugLog(`Service Worker: ${hasServiceWorker}, PushManager: ${hasPushManager}`);

    if (!hasServiceWorker || !hasPushManager) {
      setIsSupported(false);
      addDebugLog('❌ Браузер не поддерживает push');
      return;
    }

    initServiceWorker();
    checkSubscription();
    getSubscribersCount();
  }, [addDebugLog, initServiceWorker, checkSubscription, getSubscribersCount]);

  useEffect(() => {
    if (!isAuthenticated) return;

    loadOrders();
    const interval = setInterval(() => {
      loadOrders();
    }, 3000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  const subscribeToPush = async () => {
    try {
      setLoading(true);
      addDebugLog('🔔 Начинаем подписку...');

      if (isIOS && !window.navigator.standalone) {
        const msg = '⚠️ Для push-уведомлений:\n1. Нажмите "Поделиться"\n2. "На экран Домой"\n3. Запустите с главного экрана';
        alert(msg);
        addDebugLog(msg);
        setLoading(false);
        return;
      }

      addDebugLog('📢 Запрашиваем разрешение...');
      const permission = await Notification.requestPermission();
      addDebugLog(`Разрешение: ${permission}`);

      if (permission !== 'granted') {
        addDebugLog('❌ Разрешение не получено');
        alert('Для уведомлений нужно разрешение');
        setLoading(false);
        return;
      }

      addDebugLog('🔄 Получаем Service Worker...');
      const serviceWorkerReg = await navigator.serviceWorker.ready;

      addDebugLog('🔑 Конвертируем VAPID ключ...');
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

      addDebugLog('📝 Создаем подписку...');
      const sub = await serviceWorkerReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      addDebugLog('✅ Подписка создана!');

      addDebugLog('📤 Отправляем на сервер...');
      const response = await fetch(`${API_URL}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub)
      });

      if (response.ok) {
        setIsSubscribed(true);
        setSubscription(sub);
        addDebugLog('✅ Подписка сохранена!');
        alert('✅ Вы подписались на уведомления!');
        getSubscribersCount();
      } else {
        addDebugLog(`❌ Ошибка сервера: ${response.status}`);
      }
    } catch (error) {
      addDebugLog(`❌ Ошибка: ${error.message}`);
      alert('Ошибка: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    try {
      if (subscription) {
        await subscription.unsubscribe();
        setIsSubscribed(false);
        setSubscription(null);
        addDebugLog('✅ Отписались');
        alert('✅ Вы отписались от уведомлений');
        getSubscribersCount();
      }
    } catch (error) {
      addDebugLog(`Ошибка отписки: ${error.message}`);
    }
  };

  const sendTestNotification = async () => {
    if (!notificationTitle || !notificationBody) {
      alert('Заполните заголовок и текст');
      return;
    }

    setSending(true);
    try {
      addDebugLog(`📨 Отправка: ${notificationTitle}`);
      const response = await fetch(`${API_URL}/send-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: notificationTitle,
          body: notificationBody,
          icon: '/logo192.png'
        })
      });

      if (response.ok) {
        const data = await response.json();
        addDebugLog(`✅ Отправлено: ${data.message}`);
        alert(data.message);
        setNotificationTitle('');
        setNotificationBody('');
      }
    } catch (error) {
      addDebugLog(`❌ Ошибка: ${error.message}`);
      alert('Ошибка при отправке');
    } finally {
      setSending(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const getStatusText = (status) => {
    const statuses = {
      'new': '🆕 Новый',
      'processing': '⚙️ В обработке',
      'completed': '✅ Выполнен',
      'cancelled': '❌ Отменен'
    };
    return statuses[status] || '🆕 Новый';
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': '#ff9800',
      'processing': '#2196f3',
      'completed': '#4caf50',
      'cancelled': '#f44336'
    };
    return colors[status] || '#ff9800';
  };

  const stats = {
    total: orders.length,
    new: orders.filter(o => o.status === 'new' || !o.status).length,
    processing: orders.filter(o => o.status === 'processing').length,
    completed: orders.filter(o => o.status === 'completed').length
  };

  if (!isSupported) {
    return (
      <div className="App">
        <div className="App-header">
          <h1>📱 Push Уведомления</h1>
          <p>Ваш браузер не поддерживает push-уведомления</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="App">
        <div className="App-header">
          <div className="login-container">
            <h1>🔐 Вход в CRM</h1>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                placeholder="Введите пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="submit">Войти</button>
              {loginError && <p style={{ color: 'red', textAlign: 'center', marginTop: 15 }}>{loginError}</p>}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="App-header">
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          {/* Шапка */}
          <div className="crm-header">
            <h1 className="crm-title">📦 CRM - Управление заказами</h1>
            <div className="crm-buttons">
              <button onClick={loadOrders} style={{ background: '#4caf50', color: 'white' }}>🔄 Обновить</button>
              <button onClick={handleLogout} style={{ background: '#f44336', color: 'white' }}>🚪 Выйти</button>
            </div>
          </div>

          {/* Статистика */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{stats.total}</div>
              <div className="stat-label">Всего заказов</div>
            </div>
            <div className="stat-card">
              <div className="stat-number" style={{ color: '#ff9800' }}>{stats.new}</div>
              <div className="stat-label">Новые</div>
            </div>
            <div className="stat-card">
              <div className="stat-number" style={{ color: '#2196f3' }}>{stats.processing}</div>
              <div className="stat-label">В обработке</div>
            </div>
            <div className="stat-card">
              <div className="stat-number" style={{ color: '#4caf50' }}>{stats.completed}</div>
              <div className="stat-label">Выполнено</div>
            </div>
          </div>

          {/* Push-уведомления */}
          <div className="push-block">
            <h2 className="push-title">🔔 Push-уведомления</h2>
            <div className="push-info">
              <div>
                <p>📊 Подписчиков: <strong>{subscribersCount}</strong></p>
                <p>Статус: {isSubscribed ? '✅ Подписан' : '❌ Не подписан'}</p>
              </div>
              {!isSubscribed ? (
                <button onClick={subscribeToPush} disabled={loading} className="subscribe-btn">
                  {loading ? '⏳ Подписка...' : '🔔 Подписаться на уведомления'}
                </button>
              ) : (
                <button onClick={unsubscribeFromPush} className="unsubscribe-btn">
                  🔕 Отписаться
                </button>
              )}
            </div>

            {isSubscribed && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #eee' }}>
                <h3 style={{ marginBottom: 12, fontSize: '1rem', fontWeight: 500 }}>📨 Отправить тестовое уведомление</h3>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Заголовок"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    style={{ flex: 1, padding: 10, border: '1px solid #ddd', borderRadius: 8 }}
                  />
                  <textarea
                    placeholder="Текст"
                    value={notificationBody}
                    onChange={(e) => setNotificationBody(e.target.value)}
                    rows="2"
                    style={{ flex: 2, padding: 10, border: '1px solid #ddd', borderRadius: 8 }}
                  />
                  <button onClick={sendTestNotification} disabled={sending} style={{ padding: '10px 20px', background: '#4caf50', color: 'white', border: 'none', borderRadius: 8 }}>
                    {sending ? 'Отправка...' : '📨 Отправить'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Фильтры */}
          <div className="filters">
            <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Все</button>
            <button className={`filter-btn ${filter === 'new' ? 'active' : ''}`} onClick={() => setFilter('new')}>🆕 Новые</button>
            <button className={`filter-btn ${filter === 'processing' ? 'active' : ''}`} onClick={() => setFilter('processing')}>⚙️ В обработке</button>
            <button className={`filter-btn ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>✅ Выполненные</button>
          </div>

          {/* Список заказов */}
          <div className="orders-container">
            <h2 className="orders-title">📋 Заказы</h2>
            {loading && <div style={{ textAlign: 'center', padding: 40 }}>Загрузка...</div>}
            {!loading && filteredOrders.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Нет заказов</div>
            )}
            {filteredOrders.map(order => (
              <div key={order.id} className="order-card" style={{ borderLeftColor: getStatusColor(order.status) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                  <div>
                    <strong style={{ fontSize: 16 }}>Заказ #{order.id}</strong>
                    <span style={{ color: '#999', fontSize: 12, marginLeft: 10 }}>{new Date(order.createdAt).toLocaleString()}</span>
                  </div>
                  <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 'bold', background: getStatusColor(order.status), color: 'white' }}>
                    {getStatusText(order.status)}
                  </span>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <strong>👤 {order.customer?.name}</strong><br />
                  📞 {order.customer?.phone}<br />
                  📦 Доставка: {order.deliveryMethod === 'pickup' ? 'Самовывоз' : 'Доставка'}<br />
                  💳 Оплата: {order.paymentMethod === 'cash' ? 'Наличные' : 'СБП'}
                </div>

                <div style={{ marginBottom: 12 }}>
                  {order.items?.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                      <span>{item.brand} / {item.name}</span>
                      <span>{item.quantity} x {item.price}₽</span>
                    </div>
                  ))}
                </div>

                <div style={{ textAlign: 'right', fontWeight: 'bold', fontSize: 18, color: '#2196f3', marginBottom: 12 }}>
                  💸 Итого: {order.totalPrice?.toFixed(2)}₽
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => updateOrderStatus(order.id, 'new')} className="status-btn btn-new">🆕 Новый</button>
                    <button onClick={() => updateOrderStatus(order.id, 'processing')} className="status-btn btn-processing">⚙️ В обработке</button>
                    <button onClick={() => updateOrderStatus(order.id, 'completed')} className="status-btn btn-completed">✅ Выполнен</button>
                    <button onClick={() => updateOrderStatus(order.id, 'cancelled')} className="status-btn btn-cancelled">❌ Отменен</button>
                  </div>
                  <button onClick={() => deleteOrder(order.id)} style={{ padding: '6px 12px', background: '#9e9e9e', color: 'white', border: 'none', borderRadius: 6 }}>
                    🗑 Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Отладка */}
          <details style={{ marginTop: 20, background: 'white', borderRadius: 12, padding: 12 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 500, color: '#666' }}>🔧 Отладка</summary>
            <pre style={{ fontSize: 10, textAlign: 'left', overflow: 'auto', maxHeight: 150, color: '#333', marginTop: 10 }}>
              {debugInfo || 'Нет данных'}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}

export default App;
